import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { learningApi } from '../../../api/learning.js';

/**
 * Course builder — manage a course's lessons and its assessment (pass score).
 */
export default function CourseDetail() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  function refresh() {
    setLoading(true);
    learningApi
      .getCourse(id)
      .then(setDetail)
      .catch((e) => setError(e.response?.data?.message || 'Failed to load course'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, [id]);

  async function addLesson(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await learningApi.addLesson(id, { title: fd.get('title'), durationMinutes: fd.get('duration') ? Number(fd.get('duration')) : null });
      e.target.reset();
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add lesson');
    }
  }

  async function saveAssessment(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await learningApi.upsertAssessment(id, { passScore: Number(fd.get('passScore')), questions: [] });
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save assessment');
    }
  }

  const course = detail?.course;

  return (
    <section data-testid="learning-course-detail-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>{course?.title || 'Course'}</h1>
          <p className="muted">{course?.category || '—'}</p>
        </div>
        <Link className="btn ghost" to="/learning/catalog">← Catalog</Link>
      </header>

      {error && <div className="alert error" data-testid="learning-course-detail-error">{error}</div>}

      <div className="card">
        <h3>Lessons</h3>
        <table className="data-table" data-testid="learning-lesson-list">
          <thead><tr><th>#</th><th>Title</th><th>Duration</th></tr></thead>
          <tbody>
            {(detail?.lessons || []).map((l) => (
              <tr key={l.id} data-testid={`learning-lesson-row-${l.id}`}>
                <td>{l.sequence}</td><td>{l.title}</td><td>{l.duration_minutes || 0} min</td>
              </tr>
            ))}
            {detail && detail.lessons.length === 0 && <tr><td colSpan={3} className="muted">No lessons yet.</td></tr>}
          </tbody>
        </table>
        <form onSubmit={addLesson} className="inline-form">
          <input name="title" placeholder="Lesson title" data-testid="learning-lesson-title-input" required />
          <input name="duration" type="number" min="0" placeholder="min" data-testid="learning-lesson-duration-input" />
          <button className="btn primary" type="submit" data-testid="learning-lesson-add">Add lesson</button>
        </form>
      </div>

      <div className="card">
        <h3>Assessment</h3>
        <form onSubmit={saveAssessment} className="inline-form">
          <label>Pass score (%)
            <input name="passScore" type="number" min="0" max="100" defaultValue={detail?.assessment?.pass_score ?? 70} data-testid="learning-assessment-passscore-input" required />
          </label>
          <button className="btn primary" type="submit" data-testid="learning-assessment-save">Save assessment</button>
        </form>
      </div>
    </section>
  );
}
